import { useState } from "react";
import { Close, PersonAddOutlined, EmailOutlined } from "@material-ui/icons";
import styled, { keyframes } from "styled-components";

export default function AddUserModal({ onRegister, onClose }) {
    const [email, setEmail] = useState("");

    const handleSubmit = (e) => {
        e.preventDefault();
        if (email.trim()) {
            onRegister(email.trim());
        }
    };

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <HeaderIconWrapper>
                        <PersonAddOutlined style={{ fontSize: "28px" }} />
                    </HeaderIconWrapper>
                    <HeaderText>
                        <ModalTitle>Adicionar Usuário</ModalTitle>
                        <ModalSubtitle>Cadastre um novo usuário no portal</ModalSubtitle>
                    </HeaderText>
                    <CloseButton onClick={onClose} type="button">
                        <Close style={{ fontSize: "20px" }} />
                    </CloseButton>
                </ModalHeader>

                <ModalBody onSubmit={handleSubmit}>
                    <FieldGroup>
                        <FieldLabel>
                            <EmailOutlined style={{ fontSize: "16px" }} />
                            Email
                        </FieldLabel>
                        <InputWrapper>
                            <InputIcon>
                                <EmailOutlined style={{ fontSize: "20px" }} />
                            </InputIcon>
                            <StyledInput
                                name="email"
                                type="email"
                                required
                                placeholder="usuario@seidor.com.br"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoFocus
                            />
                        </InputWrapper>
                        <FieldHint>O usuário receberá um e-mail com as credenciais de acesso.</FieldHint>
                    </FieldGroup>

                    <ModalActions>
                        <CancelBtn type="button" onClick={onClose}>
                            Cancelar
                        </CancelBtn>
                        <SubmitBtn type="submit" disabled={!email.trim()}>
                            <PersonAddOutlined style={{ fontSize: "18px" }} />
                            Adicionar
                        </SubmitBtn>
                    </ModalActions>
                </ModalBody>
            </Modal>
        </Overlay>
    );
}

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from { opacity: 0; transform: translateY(20px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    animation: ${fadeIn} 0.2s ease;
`;

const Modal = styled.div`
    background: white;
    border-radius: 20px;
    width: 90%;
    max-width: 480px;
    box-shadow: 0 24px 64px rgba(15, 23, 42, 0.25);
    animation: ${slideUp} 0.3s ease;
    overflow: hidden;
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 24px 28px;
    background: linear-gradient(135deg, #f8f9ff 0%, #f0f1f8 100%);
    border-bottom: 1px solid #e8e8e8;
`;

const HeaderIconWrapper = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const HeaderText = styled.div`
    flex: 1;
`;

const ModalTitle = styled.h2`
    margin: 0 0 4px 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    font-family: "Mulish", sans-serif;
`;

const ModalSubtitle = styled.p`
    margin: 0;
    font-size: 13px;
    color: #6b7280;
    font-family: "Mulish", sans-serif;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #9ca3af;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #4b5563;
    }
`;

const ModalBody = styled.form`
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FieldLabel = styled.label`
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    font-family: "Mulish", sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const InputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const InputIcon = styled.div`
    position: absolute;
    left: 14px;
    color: #667eea;
    display: flex;
    align-items: center;
    pointer-events: none;
    opacity: 0.7;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 14px 16px 14px 48px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 15px;
    font-family: "Mulish", sans-serif;
    background: #f9fafb;
    color: #1f2937;
    transition: all 0.2s ease;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #667eea;
        background: white;
        box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
    }

    &:hover:not(:focus) {
        border-color: #d1d5db;
        background: white;
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const FieldHint = styled.span`
    font-size: 12px;
    color: #9ca3af;
    font-family: "Mulish", sans-serif;
    padding-left: 2px;
`;

const ModalActions = styled.div`
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding-top: 4px;
`;

const CancelBtn = styled.button`
    border: none;
    border-radius: 12px;
    padding: 12px 24px;
    font-size: 14px;
    font-weight: 600;
    font-family: "Mulish", sans-serif;
    background: #f3f4f6;
    color: #4b5563;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #e5e7eb;
        color: #1f2937;
    }
`;

const SubmitBtn = styled.button`
    border: none;
    border-radius: 12px;
    padding: 12px 28px;
    font-size: 14px;
    font-weight: 600;
    font-family: "Mulish", sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 14px rgba(102, 126, 234, 0.3);

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
    }
`;
